import { useCallback, useEffect, useRef, useState, type FC } from "react";
import HotspotContent from "./HotspotContent/HotspotContent";

import { cn } from "@babybjorn/ui";

import type { ISbRichtext } from "gatsby-source-storyblok";
import type { LinkProps } from "../../../Link/Link";
import { useIsTablet } from "../../../../../hooks/use-is-screensize";
import ImageContainer from "../../../ImageContainers/ImageContainer";
import * as s from "./Hotspot.module.scss";
import { pushCustomGTMEvent } from "../../../../../utils/gtm/gtm-handlers";

export type HotspotProps = {
	label?: string;
	title?: string;
	content?: string | ISbRichtext | undefined;
	link?: LinkProps;
	image?: { filename: string; alt: string };
	xPosition: string | null;
	yPosition: string | null;
	xPositionMobile?: string | null;
	yPositionMobile?: string | null;
	productGallery?: boolean;
};

type AdditionalProps = {
	activeIndex: number | null;
	setActiveIndex: (value: number | null) => void;
	index: number;
	id?: string;
	name?: string;
};

const Hotspot: FC<HotspotProps & AdditionalProps> = ({
	title,
	content,
	link,
	image,
	xPosition: xPositionDefault,
	yPosition: yPositionDefault,
	xPositionMobile,
	yPositionMobile,
	index,
	activeIndex,
	setActiveIndex,
	productGallery,
}) => {
	const hotspotRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const isTablet = useIsTablet();
	const [xPosition, setXPosition] = useState<string | null>(xPositionDefault);
	const [yPosition, setYPosition] = useState<string | null>(yPositionDefault);
	const [contentPos, setContentPos] = useState<{
		top: number | string;
		left: number | string;
	}>({
		top: 0,
		left: 0,
	});

	useEffect(() => {
		if (xPositionMobile) {
			if (isTablet) {
				setXPosition(xPositionDefault);
			} else {
				setXPosition(xPositionMobile);
			}
		}
		if (yPositionMobile) {
			if (isTablet) {
				setYPosition(yPositionDefault);
			} else {
				setYPosition(yPositionMobile);
			}
		}
	}, [
		isTablet,
		xPositionMobile,
		yPositionMobile,
		xPositionDefault,
		yPositionDefault,
	]);

	const resolvePlacement = useCallback(() => {
		if (
			typeof window !== "undefined" &&
			hotspotRef.current?.parentElement &&
			contentRef.current?.clientWidth
		) {
			const boxWidth = contentRef.current.clientWidth;
			const boxHeight = contentRef.current.clientHeight;

			const parentWidth = hotspotRef.current.parentElement.clientWidth;
			const parentHeight = hotspotRef.current.parentElement.clientHeight;

			const xPx = (parentWidth || 0) * Number.parseInt(xPosition || "0") * 0.01;
			const yPx =
				(parentHeight || 0) * Number.parseInt(yPosition || "0") * 0.01;

			const xMid = Number.parseInt(xPosition || "0") - 50;

			const fromXMid = (parentWidth || 0) * xMid * 0.01;

			let isCollidingTop = false;
			if (yPx - boxHeight - 20 < 0) {
				isCollidingTop = true;
			}

			let isCollidingLeft = false;
			if (xPx - boxWidth - 20 < 0) {
				isCollidingLeft = true;
			}

			let isCollidingBottom = false;
			if (yPx + boxHeight + 20 > parentHeight) {
				isCollidingBottom = true;
			}

			let isCollidingRight = false;
			if (xPx + boxWidth + 20 > parentWidth) {
				isCollidingRight = true;
			}

			if (parentWidth <= 350) {
				return isCollidingTop
					? setContentPos({
							top: 50,
							left: -fromXMid + 24 - boxWidth / 2,
						})
					: setContentPos({
							top: -boxHeight - 5,
							left: -fromXMid + 24 - boxWidth / 2,
						});
			}

			if (
				!isCollidingTop &&
				!isCollidingLeft &&
				!isCollidingBottom &&
				!isCollidingRight
			) {
				return setContentPos({
					top: (boxHeight + 10) * -1,
					left: -boxWidth / 2 + 20,
				});
			}

			if (isCollidingTop) {
				if (isCollidingLeft) {
					return isCollidingRight
						? setContentPos({
								top: 50,
								left: -boxWidth / 2 + 20,
							})
						: setContentPos({
								top: 0,
								left: 50,
							});
				} else if (isCollidingRight) {
					return setContentPos({
						top: 0,
						left: -boxWidth - 10,
					});
				} else {
					return setContentPos({
						top: 50,
						left: -boxWidth / 2 + 20,
					});
				}
			}

			if (isCollidingLeft) {
				if (isCollidingBottom) {
					return isCollidingRight
						? setContentPos({
								top: (boxHeight + 10) * -1,
								left: -boxWidth / 2 + 20,
							})
						: setContentPos({
								top: -boxHeight + 40,
								left: 50,
							});
				} else if (isCollidingRight) {
					return setContentPos({
						top: (boxHeight + 10) * -1,
						left: -boxWidth / 2 + 20,
					});
				} else {
					return setContentPos({
						top: -boxHeight / 2 + 20,
						left: 50,
					});
				}
			}

			if (isCollidingBottom) {
				return isCollidingRight
					? setContentPos({
							top: -boxHeight + 40,
							left: -boxWidth - 10,
						})
					: setContentPos({
							top: -boxHeight - 10,
							left: -boxWidth / 2 + 20,
						});
			}

			if (isCollidingRight) {
				return setContentPos({
					top: -boxHeight / 2 + 20,
					left: -boxWidth - 10,
				});
			}
		}
	}, [xPosition, yPosition]);

	useEffect(() => {
		if (activeIndex !== null) {
			setTimeout(() => {
				resolvePlacement();
			}, 0);
		}

		window.addEventListener("resize", resolvePlacement);

		return () => {
			window.removeEventListener("resize", resolvePlacement);
		};
	}, [activeIndex, resolvePlacement]);

	return (
		<div
			ref={hotspotRef}
			className={cn(s.wrapper)}
			style={{
				top: `${yPosition}%`,
				left: `${xPosition}%`,
				zIndex: activeIndex === index ? 1 : 0,
			}}
		>
			<button
				className={cn(s.hotspot)}
				onClick={() => {
					if (activeIndex === index) {
						setActiveIndex(null);
					} else {
						pushCustomGTMEvent({
							type: "hotspotClick",
						});
						setActiveIndex(index);
					}
				}}
			/>
			<span />
			{activeIndex === index && (
				<HotspotContent
					ref={contentRef}
					headline={title || ""}
					hotspotContent={content || ""}
					image={
						image?.filename ? (
							<ImageContainer
								ratio="square"
								objectFit="cover"
								imgSrc={image.filename}
								alt={image.alt}
							/>
						) : undefined
					}
					link={link}
					style={{
						top: contentPos.top + "px",
						left: contentPos.left + "px",
					}}
					productGallery={productGallery}
				/>
			)}
		</div>
	);
};

export default Hotspot;
